import { useState, useEffect, useContext } from "react";
import {
  Grid,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Box,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { useParams, useNavigate, Link } from "react-router-dom";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import { EventContext } from "../../contexts/EventContext";
import OrgSelect from "../../components/OrgSelect";

const Payouts = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { userData } = useContext(EventContext);
  const [payouts, setPayouts] = useState([]);
  const [totalPending, setTotalPending] = useState("");
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [confirmPayout, setConfirmPayout] = useState(null);
  const [confirmResend, setConfirmResend] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    if (orgId) getPayouts();
  }, [orgId, status]);

  const getPayouts = async () => {
    try {
      setLoading(true);
      const result = await request(`payouts?orgId=${orgId}&status=${status}`);
      if (result.success) {
        setPayouts(result.data.payouts);
        setTotalPending(result?.data?.totalPending);
      } else {
        setPayouts([]);
      }
    } catch (err) {
      toast.error("Failed to load payouts");
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = (newOrgId) => {
    if (!newOrgId) return;
    navigate(`/payouts/${newOrgId}`);
  };

  const makePayout = async (payout) => {
    const body = { eventId: payout.eventId };
    const result = await request("payouts/transfer", "post", body);
    if (result.success) {
      toast.success("Transfer initiated");
    }
  };

  const resendConfirmation = async (payout) => {
    try {
      setResendingId(payout.eventId);
      const result = await request("payouts/resend-confirmation", "post", {
        eventId: payout.eventId,
      });
      if (result.success) {
        toast.success("Confirmation resent");
      } else {
        toast.error(result.error || "Failed to resend confirmation");
      }
    } catch (err) {
      toast.error("Failed to resend confirmation");
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Link
          to="/admin"
          style={{
            color: "#ff3d00",
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 500,
            marginLeft: 20,
          }}
        >
          BACK TO ADMIN
        </Link>

        <Typography variant="h5" align="center">
          Payouts
        </Typography>

        {/* right-side spacer to keep title centered */}
        <Box sx={{ width: 110 }} />
      </Box>
      {userData?.role === "superadmin" && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "center",
            width: "100%",
            maxWidth: {
              xs: "100%",
              sm: 600,
              md: 700,
            },
            mx: "auto",
          }}
        >
          <OrgSelect org={orgId} setOrg={handleOrgChange} size="small" />
        </Box>
      )}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={(e, val) => {
            if (val) setStatus(val);
          }}
          size="small"
        >
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && payouts.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 4 }}
        >
          No {status} payouts found.
        </Typography>
      )}
      {totalPending && (
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          sx={{ mt: 4, mb: 4 }}
        >
          Total Pending Payout Value: ${totalPending}
        </Typography>
      )}
      {payouts.map((p) => (
        <Paper
          key={p.eventId}
          elevation={2}
          sx={{
            p: 3,
            mb: 2,
            width: "100%",
            maxWidth: {
              xs: "100%",
              sm: 600,
              md: 700,
            },
            mx: "auto",
          }}
        >
          {p.invoiceRequired === true && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="error">
                Please create an invoice for this event prior to paying out.
              </Typography>
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography variant="h6">{p.eventName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Event Date: {new Date(p.eventDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Event Status: {p.eventStatus}
                  </Typography>
                </Box>

                {status === "completed" && p.transferredAt && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Chip
                      color="success"
                      size="small"
                      label={`Paid ${new Date(p.transferredAt).toLocaleDateString()}`}
                    />
                    {userData?.role === "superadmin" && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={resendingId === p.eventId}
                        onClick={() => setConfirmResend(p)}
                      >
                        {resendingId === p.eventId
                          ? "Sending..."
                          : "Resend Confirmation"}
                      </Button>
                    )}
                  </Box>
                )}
                {userData?.role === "superadmin" &&
                  p.eventStatus === "completed" &&
                  status === "pending" && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={p.invoiceRequired === true}
                        onClick={() => setConfirmPayout(p)}
                      >
                        Payout
                      </Button>
                    </Box>
                  )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Gross Sales</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">
                ${p.grossSales.toFixed(2)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Tax Collected</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">
                ${p.tax.toFixed(2)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Ticket Fees</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">
                -${p.fees.toFixed(2)}
              </Typography>
            </Grid>

            {typeof p.invoiceTotal === "number" && p.invoiceId && (
              <>
                <Grid item xs={6}>
                  <Link
                    to={`/invoice/${p.invoiceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Typography variant="body2" color="primary">
                      Platform Fees
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={6}>
                  <Link
                    to={`/invoice/${p.invoiceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Typography variant="body2" align="right" color="primary">
                      -${p.invoiceTotal.toFixed(2)}
                    </Typography>
                  </Link>
                </Grid>
              </>
            )}

            {p.refunds > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Refunds</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    -${p.refunds.toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {status === "completed" ? (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Paid Out
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle1"
                    align="right"
                    sx={{ fontWeight: 600 }}
                  >
                    ${p.paidOut.toFixed(2)}
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Net Payable
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle1"
                    align="right"
                    sx={{ fontWeight: 600 }}
                  >
                    ${p.netPayable.toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    align="right"
                    color="text.secondary"
                  >
                    ${p.pending.toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Balance
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    align="right"
                    color="text.secondary"
                  >
                    ${p.remainingBalance.toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      ))}
      <Dialog
        open={Boolean(confirmPayout)}
        onClose={() => setConfirmPayout(null)}
      >
        <DialogTitle>Confirm Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to payout funds for{" "}
            <strong>{confirmPayout?.eventName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPayout(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              makePayout(confirmPayout);
              setConfirmPayout(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmResend)}
        onClose={() => setConfirmResend(null)}
      >
        <DialogTitle>Resend Payout Confirmation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Resend the payout confirmation email for{" "}
            <strong>{confirmResend?.eventName}</strong> to the org's billing
            contacts?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResend(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = confirmResend;
              setConfirmResend(null);
              resendConfirmation(target);
            }}
          >
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;
